import { useMutation, useQueryClient } from '@tanstack/react-query'

import { coachingKeys } from 'modules/coaching/query-keys'
import {
  createConversationFollowUpUpload,
  createConversationUpload,
  finalizeConversationUploadForLocalDevelopment,
  markFileUploadFailed,
  uploadFileToSignedUrl,
} from 'modules/files/api'
import { PERCENT_SCALE } from 'utils/units'

import type { CreateConversationFollowUpUploadResponse, CreateConversationResponse201 } from '@startech/openapi'

type PrepareUploadMutationParameters = {
  duration?: number
  filename: string
  mimeType: string
  sizeBytes: number
}

type UploadPendingVideoMutationParameters = {
  conversationId: string
  file: File
  fileId?: string
  signal?: AbortSignal
  uploadUrl: string
  onProgress?: (percentage: number, phase: 'uploading') => void
}

type UploadFollowUpVideoMutationParameters = {
  conversationId: string
  duration?: number
  file: File
  signal?: AbortSignal
  onProgress?: (percentage: number, phase: 'uploading') => void
}

const STORAGE_UPLOAD_PROGRESS_SHARE = 95

const uploadPreparedConversationVideo = async (parameters: {
  conversationId: string
  file: File
  fileId?: string
  signal?: AbortSignal
  uploadUrl: string
  onProgress?: (percentage: number, phase: 'uploading') => void
}): Promise<void> => {
  const { conversationId, file, fileId, onProgress, signal, uploadUrl } = parameters

  await uploadFileToSignedUrl(
    uploadUrl,
    file,
    percentage => {
      onProgress?.(Math.round((percentage * STORAGE_UPLOAD_PROGRESS_SHARE) / PERCENT_SCALE), 'uploading')
    },
    signal
  )
  await finalizeConversationUploadForLocalDevelopment(conversationId, fileId, signal)
  onProgress?.(PERCENT_SCALE, 'uploading')
}

const waitForUploadProcessing = async (parameters: UploadPendingVideoMutationParameters) => {
  await uploadPreparedConversationVideo(parameters)

  return null
}

export const usePrepareUploadMutation = () => {
  return useMutation<CreateConversationResponse201, Error, PrepareUploadMutationParameters>({
    mutationFn: input => createConversationUpload(input),
  })
}

const uploadFollowUpVideo = async (
  parameters: UploadFollowUpVideoMutationParameters
): Promise<CreateConversationFollowUpUploadResponse> => {
  const { conversationId, duration, file, onProgress, signal } = parameters
  let uploadContext: CreateConversationFollowUpUploadResponse | undefined

  try {
    uploadContext = await createConversationFollowUpUpload(conversationId, {
      duration,
      filename: file.name,
      mimeType: file.type || 'video/mp4',
      sizeBytes: file.size,
    })

    await uploadPreparedConversationVideo({
      conversationId,
      file,
      fileId: uploadContext.fileId,
      onProgress,
      signal,
      uploadUrl: uploadContext.uploadUrl,
    })

    return uploadContext
  } catch (error) {
    if (uploadContext) {
      const message = error instanceof Error ? error.message : 'Follow-up upload failed'
      await markFileUploadFailed(uploadContext.fileId, message, signal).catch(() => undefined)
    }
    throw error
  }
}

const useUploadPendingVideoMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<null, Error, UploadPendingVideoMutationParameters>({
    mutationFn: waitForUploadProcessing,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: coachingKeys.conversationDetail(variables.conversationId),
      })
      void queryClient.invalidateQueries({
        queryKey: coachingKeys.conversationMessages(variables.conversationId),
      })
    },
  })
}

export const useFollowUpVideoUploadMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<CreateConversationFollowUpUploadResponse, Error, UploadFollowUpVideoMutationParameters>({
    mutationFn: uploadFollowUpVideo,
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: coachingKeys.conversationDetail(variables.conversationId),
      })
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: coachingKeys.conversationMessages(variables.conversationId),
      })
    },
  })
}

export const usePendingUploadWorkflow = () => {
  const { mutateAsync, isPending } = useUploadPendingVideoMutation()

  const startPendingUpload = async (
    parameters: UploadPendingVideoMutationParameters,
    handlers: {
      onError?: (message: string) => void
      onProgress?: (progress: { percentage: number; phase: 'uploading' }) => void
      onStart?: () => void
      onSuccess?: () => void
    } = {}
  ) => {
    handlers.onStart?.()

    try {
      await mutateAsync({
        ...parameters,
        onProgress: (percentage, phase) => {
          handlers.onProgress?.({ percentage, phase })
          parameters.onProgress?.(percentage, phase)
        },
      })

      handlers.onSuccess?.()
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }

      const message = error instanceof Error ? error.message : 'Upload failed'
      handlers.onError?.(message)
      throw error
    }
  }

  return {
    isPending,
    startPendingUpload,
  }
}
